"use client";

import { ComponentProps, ReactNode, useEffect, useState } from "react";
import { Button } from "@zapier/design-system";

type ButtonOnPress = NonNullable<ComponentProps<typeof Button>["onPress"]>;
type PressEvent = Parameters<ButtonOnPress>[0];
import {
  emitUserInteractionEvent,
  UserInteractionEventName,
} from "src/observability/tracking";
import { useLocalStorage } from "src/hooks/useLocalStorage";
import { LOCALSTORAGE_TEMPLATE_CREATE_KEY } from "src/constants/constants";
import { GoogleLogoSvg } from "./GoogleLogoSvg";
import styles from "./GoogleSSOButton.module.css";

const createGoogleSSOUrl = ({
  baseUrl,
  search,
  pathname,
}: {
  baseUrl: string;
  pathname: string;
  search: string;
}) => {
  const parsedUrl = new URL("/google-sso/start", baseUrl);
  parsedUrl.searchParams.set("scopes", "basic");

  let nextParam = pathname;

  if (search) {
    nextParam += `${search}&create=true`;
  } else {
    nextParam += "?create=true";
  }

  parsedUrl.searchParams.set("next", nextParam);

  return parsedUrl.toString();
};

type GoogleSSOButtonProps = {
  onPress?: (event: PressEvent) => void;
  children: ReactNode;
  slug: string;
  size?: "small" | "medium";
  templateId?: string;
};

export const GoogleSSOButton = ({
  onPress,
  children,
  slug,
  templateId = "",
  size = "medium",
  ...rest
}: GoogleSSOButtonProps & ComponentProps<typeof Button>) => {
  const { setValue: setLocalStorageTemplateCreateKey } =
    useLocalStorage<string>(LOCALSTORAGE_TEMPLATE_CREATE_KEY);

  const [ssoStartUrl, setSsoStartUrl] = useState<string>();

  useEffect(() => {
    const url = createGoogleSSOUrl({
      baseUrl: window.location.origin,
      pathname: `/templates/details/${slug}`,
      search: window.location.search,
    });
    setSsoStartUrl(url);
  }, [slug]);

  const handlePress = (event: PressEvent) => {
    emitUserInteractionEvent(UserInteractionEventName.googleSSOButtonClicked, {
      event_action: "click",
      option_selected: slug,
      custom_tracking_fields: {
        template_id: templateId,
      },
    });

    setLocalStorageTemplateCreateKey(slug);

    if (onPress) {
      onPress(event);
    }

    if (ssoStartUrl) {
      window.location.assign(ssoStartUrl);
    }
  };

  return (
    <div className={styles.buttonWrapper}>
      <Button size={size} variant="tertiary" onPress={handlePress} {...rest}>
        <span className={styles.buttonContent}>
          <GoogleLogoSvg />
          {children}
        </span>
      </Button>
    </div>
  );
};
